import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { hapticTap } from "@/lib/native-haptics";

const EDGE_PX = 24;            // touch must start within left 24px
const COMMIT_RATIO = 0.4;       // dragged >40% width = commit
const COMMIT_VELOCITY = 0.6;    // px/ms
const MAX_PULL = 1;             // 100% width

/**
 * Phase 2 - iOS-style edge swipe-back.
 *
 * Listens for touches starting near the left edge of the viewport,
 * tracks horizontal drag, and calls `navigate(-1)` once the user
 * passes the commit threshold or flicks fast enough.
 *
 * Live page transform is applied to `targetRef.current` so the page
 * follows the finger; on release we either commit (let the pop
 * transition take over) or snap back.
 */
export function useEdgeSwipeBack(
  targetRef: React.RefObject<HTMLElement>,
  enabled = true
) {
  const navigate = useNavigate();
  const state = useRef<{
    active: boolean;
    startX: number;
    startY: number;
    startT: number;
    width: number;
  } | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let isNative = false;
    try { isNative = Capacitor.isNativePlatform(); } catch { /* ignore */ }
    if (!isNative) return;
    if (typeof window === "undefined") return;

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      const t = e.touches[0];
      if (t.clientX > EDGE_PX) return;
      // Only first history entry can't go back
      if (window.history.length <= 1) return;
      state.current = {
        active: true,
        startX: t.clientX,
        startY: t.clientY,
        startT: performance.now(),
        width: window.innerWidth,
      };
    };

    const onTouchMove = (e: TouchEvent) => {
      const s = state.current;
      if (!s || !s.active) return;
      const t = e.touches[0];
      const dx = t.clientX - s.startX;
      const dy = t.clientY - s.startY;
      // Cancel if user drags more vertically than horizontally early on
      if (Math.abs(dy) > Math.abs(dx) + 8 && Math.abs(dx) < 12) {
        s.active = false;
        if (targetRef.current) targetRef.current.style.transform = "";
        return;
      }
      if (dx <= 0) return;
      e.preventDefault();
      const pct = Math.min(MAX_PULL, dx / s.width);
      const el = targetRef.current;
      if (el) {
        el.style.transform = `translate3d(${dx}px,0,0)`;
        el.style.transition = "none";
        el.style.willChange = "transform";
      }
      // Optional: dim background - left to CSS via [data-swipe-back] hook
      document.body.dataset.swipeBack = String(pct.toFixed(2));
    };

    const release = (commit: boolean) => {
      const el = targetRef.current;
      if (el) {
        el.style.transition = "transform 220ms cubic-bezier(.22,.61,.36,1)";
        el.style.transform = commit
          ? `translate3d(${window.innerWidth}px,0,0)`
          : "translate3d(0,0,0)";
        // Clean up after transition
        const cleanup = () => {
          el.style.transition = "";
          el.style.transform = "";
          el.style.willChange = "";
          el.removeEventListener("transitionend", cleanup);
        };
        el.addEventListener("transitionend", cleanup);
      }
      delete document.body.dataset.swipeBack;
      if (commit) {
        hapticTap();
        // Defer to next frame so the slide-out animation is seen
        requestAnimationFrame(() => navigate(-1));
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      const s = state.current;
      if (!s || !s.active) {
        state.current = null;
        return;
      }
      const t = e.changedTouches[0];
      const dx = t.clientX - s.startX;
      const dt = performance.now() - s.startT;
      const v = dx / Math.max(1, dt);
      const commit = dx / s.width > COMMIT_RATIO || v > COMMIT_VELOCITY;
      release(commit);
      state.current = null;
    };

    const onTouchCancel = () => {
      const s = state.current;
      if (s?.active) release(false);
      state.current = null;
    };

    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchmove", onTouchMove, { passive: false });
    document.addEventListener("touchend", onTouchEnd, { passive: true });
    document.addEventListener("touchcancel", onTouchCancel, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
      document.removeEventListener("touchcancel", onTouchCancel);
    };
  }, [enabled, navigate, targetRef]);
}

export default useEdgeSwipeBack;
